/**
 * @fileoverview Conflict-check party table (MOR-992)
 *
 * Purpose: the parties of one schedule group as a dense, scannable table —
 * one row per PPSR registration, the party name on its first row only, the
 * PMSI / non-PMSI distinction as a badge column, then the collateral class.
 * A voter scans names down the left and the badge column tells them at a
 * glance which parties hold a general security interest.
 *
 * The panel this sits in can be narrow, so the name column takes whatever
 * width is left and wraps rather than truncating — the name is the thing a
 * voter is matching against. Registration number, start date and collateral
 * description are deliberately off the face of the table (they ran to
 * paragraphs); all three sit behind a focusable disclosure button in the
 * Collateral cell (an accessible tooltip, not just a `title` attribute — a
 * `title` on a non-focusable row is invisible to keyboard and touch users)
 * and in the attached CSV.
 *
 * Groups without PPSR detail collapse to a single name column. The table
 * renders in full rather than inside its own scroll region — a nested
 * scroller inside the scrolling panel hid the first rows under the header.
 * Large groups are kept in check by starting collapsed (see the group).
 *
 * Used by: conflict-party-group.tsx
 */

import { Info } from "lucide-react";

import type { ConflictScheduledParty } from "@cvx/preAppointment/conflictChecks/schema";
import {
  collateralClassShortLabel,
  flattenScheduleRows,
  formatAuDateIso,
} from "@cvx/preAppointment/conflictBriefFormat";
import { Badge } from "@/ui/primitives/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";

interface ConflictPartyTableProps {
  parties: ConflictScheduledParty[];
  /** Whether any party carries registrations — drives the extra columns. */
  hasDetail: boolean;
}

/**
 * @component
 * @example
 * <ConflictPartyTable parties={parties} hasDetail />
 */
export function ConflictPartyTable({
  parties,
  hasDetail,
}: ConflictPartyTableProps) {
  const rows = flattenScheduleRows(parties);
  const headerCell =
    "px-2.5 py-1.5 text-left text-[11px] font-medium text-muted-foreground";

  return (
    <TooltipProvider>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full table-fixed border-collapse text-xs">
          <thead className="bg-muted">
            <tr className="border-b">
              <th scope="col" className={headerCell}>
                Party
              </th>
              {hasDetail && (
                <>
                  <th scope="col" className={cn(headerCell, "w-[5.25rem]")}>
                    Interest
                  </th>
                  <th scope="col" className={cn(headerCell, "w-[7rem]")}>
                    Collateral
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-card">
            {rows.map((row, index) => {
              const registration = row.registration;
              const detail = registration
                ? [
                    registration.registrationNumber
                      ? `Reg ${registration.registrationNumber}`
                      : null,
                    formatAuDateIso(registration.startDate)
                      ? `Registered ${formatAuDateIso(registration.startDate)}`
                      : null,
                    registration.collateralDescription?.trim() || null,
                  ]
                    .filter((part): part is string => part !== null)
                    .join("\n")
                : undefined;
              return (
                <tr
                  key={index}
                  className={cn(
                    "align-top",
                    row.firstOfParty &&
                      index > 0 &&
                      "border-t border-border/60",
                  )}
                >
                  <td
                    className={cn(
                      "break-words px-2.5 py-1.5 font-medium",
                      !row.firstOfParty && "pt-0",
                    )}
                  >
                    {row.firstOfParty ? row.name : ""}
                  </td>
                  {hasDetail && (
                    <>
                      <td
                        className={cn(
                          "px-2.5 py-1",
                          !row.firstOfParty && "pt-0",
                        )}
                      >
                        {registration &&
                          (registration.pmsi ? (
                            <Badge
                              color="green"
                              variant="soft"
                              size="sm"
                              className="whitespace-nowrap"
                            >
                              PMSI
                            </Badge>
                          ) : (
                            <Badge
                              color="amber"
                              variant="soft"
                              size="sm"
                              className="whitespace-nowrap"
                            >
                              Non-PMSI
                            </Badge>
                          ))}
                      </td>
                      <td
                        className={cn(
                          "break-words px-2.5 py-1.5 text-muted-foreground",
                          !row.firstOfParty && "pt-0",
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {registration
                            ? (collateralClassShortLabel(
                                registration.collateralClass,
                              ) ?? "")
                            : "No registration matched"}
                          {detail && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex size-4 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
                                  aria-label={`Registration details for ${row.name}`}
                                >
                                  <Info className="size-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs whitespace-pre-line text-left text-xs"
                              >
                                {detail}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
